/*
 * ShiftNotesPanel — shared closing→opening team log. Every post is stamped
 * with the author name; staff can delete their own notes, admins can delete
 * any. Auto-refreshes once a minute so notes from other tills show up.
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { MessageSquare, Send, Loader2, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMin = Math.round((now - d) / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    return d.toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function ShiftNotesPanel({ authHeaders, userId, isAdmin, toast }) {
  const [notes, setNotes] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/staff/shift-notes`, { headers: authHeaders });
      setNotes(data.items || []);
    } catch { /* silent */ } finally {
      setBusy(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);
  // Refresh once a minute so incoming notes from other tills show up.
  useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const post = async () => {
    const clean = msg.trim();
    if (!clean) return;
    setPosting(true);
    try {
      await axios.post(`${API}/staff/shift-notes`, { message: clean }, { headers: authHeaders });
      setMsg("");
      toast({ title: "Note posted", description: "The next shift will see it." });
      load();
    } catch (e) {
      toast({ title: "Post failed", description: e?.response?.data?.detail || e.message });
    } finally {
      setPosting(false);
    }
  };

  const remove = async (note) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await axios.delete(`${API}/staff/shift-notes/${note.id}`, { headers: authHeaders });
      toast({ title: "Note deleted" });
      load();
    } catch (e) {
      toast({ title: "Delete failed", description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div data-testid="shift-notes-panel">
      <div className="section-label mb-3 flex items-center gap-2">
        <MessageSquare size={13} /> Shift notes
      </div>
      <div className="p-3 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] mb-3">
        <textarea
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          rows={2}
          maxLength={1000}
          placeholder="Leave a note for the next shift…"
          data-testid="shift-note-input"
          className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-3 py-2 text-sm focus:border-[var(--mm-accent)] outline-none resize-none"
        />
        <div className="flex items-center justify-between mt-2 gap-2">
          <div className="text-[0.6rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)]">{msg.length}/1000</div>
          <button
            onClick={post}
            disabled={!msg.trim() || posting}
            data-testid="shift-note-submit"
            className="btn-primary inline-flex items-center gap-2 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {posting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Post note
          </button>
        </div>
      </div>

      {busy && notes.length === 0 ? (
        <div className="py-4 text-center"><Loader2 size={18} className="mx-auto text-[var(--mm-accent)] animate-spin" /></div>
      ) : notes.length === 0 ? (
        <div className="p-6 text-center border border-dashed border-[var(--mm-line)] text-[var(--mm-cream-muted)] text-sm">
          No notes yet. Leave a heads-up for the next shift.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="shift-notes-list">
          {notes.map((n) => {
            const canDelete = isAdmin || n.author_id === userId;
            return (
              <li key={n.id} data-testid={`shift-note-${n.id}`} className="p-3 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]">
                <div className="text-sm text-[var(--mm-cream)] whitespace-pre-wrap break-words">{n.message}</div>
                <div className="flex items-center justify-between mt-2 gap-3">
                  <div className="text-[0.6rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)]">
                    {n.author_name}{n.author_is_admin ? " · owner" : ""} · {fmtWhen(n.created_at)}
                  </div>
                  {canDelete && (
                    <button
                      onClick={() => remove(n)}
                      data-testid={`shift-note-delete-${n.id}`}
                      className="text-[0.6rem] tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] hover:text-red-400 inline-flex items-center gap-1"
                    >
                      <Trash2 size={11} /> Delete
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
